'use client';

import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

async function pushError(response: Response, fallback: string) {
  try {
    const body = await response.json();
    if (typeof body.error === 'string') return body.error;
  } catch { /* A proxy may return a non-JSON error. */ }
  return `${fallback} (HTTP ${response.status})`;
}

export default function PushNotifications({ silent = false }: { silent?: boolean }) {
  const { user } = useAuth();
  const [key, setKey] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const supported = window.isSecureContext && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(supported);
    if (!supported) {
      setMessage('Use HTTPS and a supported browser. On iPhone or iPad, add BizzBox to your Home Screen and open it there.');
      return;
    }
    setEnabled(false);
    fetch('/api/push').then(async (response) => {
      if (!response.ok) throw new Error(await pushError(response, 'Could not check notification settings.'));
      const config = await response.json();
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (cancelled) return;
      setKey(config.publicKey);
      setEnabled(Boolean(config.enabled && subscription && Notification.permission === 'granted'));
      setMessage(!config.publicKey ? 'Notifications will be available once server setup is complete.' : 'Get notified about new chats on this device.');
      if (config.enabled && subscription && Notification.permission === 'granted') {
        await fetch('/api/push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(subscription.toJSON()) });
      }
    }).catch((error) => { if (!cancelled) setMessage(error.message); });
    return () => { cancelled = true; };
  }, [user?.id]);

  async function toggle() {
    if (busy || !key) return;
    setBusy(true);
    try {
      // Request permission immediately from the user's click (required on iOS).
      if (!enabled && await Notification.requestPermission() !== 'granted') {
        setMessage('Notifications are blocked. Allow them in your browser or device settings, then try again.');
        return;
      }
      await navigator.serviceWorker.register('/sw.js');
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Notifications are taking too long to start. Reload and try again.')), 15000)),
      ]);
      let subscription = await registration.pushManager.getSubscription();
      if (enabled) {
        const response = await fetch('/api/push', { method: 'DELETE' });
        if (!response.ok) throw new Error(await pushError(response, 'Could not disable notifications.'));
        setEnabled(false);
        await subscription?.unsubscribe();
        setMessage('Notifications are off on this device.');
      } else {
        const bytes = Uint8Array.from(atob(key.replace(/-/g, '+').replace(/_/g, '/')), (char) => char.charCodeAt(0));
        subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: bytes });
        const response = await fetch('/api/push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(subscription.toJSON()) });
        if (!response.ok) throw new Error(await pushError(response, 'Could not save notification settings.'));
        setEnabled(true);
        setMessage('New message notifications are enabled on this device.');
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Could not update notifications.');
    } finally { setBusy(false); }
  }

  if (!user || silent) return null;
  return (
    <section className="mb-4 rounded-xl border border-border bg-card p-3 flex flex-wrap items-center justify-between gap-3" aria-label="Push notifications">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">Message notifications</p>
        <p role="status" className="text-xs text-muted-foreground mt-1">{message}</p>
      </div>
      <button onClick={toggle} disabled={busy || !supported || !key} className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground disabled:opacity-50">
        {busy ? 'Please wait...' : enabled ? 'Disable notifications' : 'Enable notifications'}
      </button>
    </section>
  );
}
